import { useForm } from '@tanstack/react-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  type ApiError,
  type IndicatorFromSuggestionCreate,
  type IndicatorFromSuggestionUnitsCreate,
  type IndicatorSuggestionDetailsPublic,
  LibraryService,
} from '@/client';
import useCustomToast from '@/hooks/use-custom-toast';
import { handleError } from '@/utils';

export const useIndicatorSuggestionAddForm = ({
  slug,
  suggestion,
  onSubmit,
  onSuccess,
}: {
  slug: string;
  suggestion?: IndicatorSuggestionDetailsPublic | null;
  onSubmit?: (values: IndicatorFromSuggestionCreate) => void;
  onSuccess?: () => void;
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const showToast = useCustomToast();

  const mutation = useMutation({
    mutationFn: (data: IndicatorFromSuggestionCreate) =>
      LibraryService.createIndicatorFromSuggestion({
        companySlug: slug,
        id: suggestion?.id ?? '',
        requestBody: data,
      }),
    onSuccess: () => {
      showToast(t('messages.toasts.success.title'), t('messages.toasts.success.indicatorCreated'), 'success');
      onSuccess?.();
    },
    onError: (err: ApiError) => {
      console.error(err);
      handleError(err, showToast);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['indicators'] });
    },
  });

  return useForm({
    defaultValues: {
      unit: '',
      commitment_id: null as string | null,
      units:
        suggestion?.formula_units_per_indicator?.map((el) => ({
          unit: '',
          suggestion_id: el.suggestion_id,
        })) ?? ([] as IndicatorFromSuggestionUnitsCreate[]),
    },
    validators: {
      onChange: ({ value }) => ({
        fields: {
          unit:
            suggestion && suggestion.lt_indicator_category === 'simple' && value.unit.length === 0
              ? t('indicator.columnsHeader.unit.required')
              : undefined,
          'units[${index}].unit':
            suggestion && suggestion.lt_indicator_category === 'simple' && value.unit.length === 0
              ? t('indicator.columnsHeader.unit.required')
              : undefined,
        },
      }),
    },
    onSubmit: (values) => {
      const _values = {
        unit: values.value.unit,
        units: values.value.units,
        commitment_id: values.value.commitment_id ?? null,
      };
      mutation.mutate(_values);
      onSubmit?.(_values);
    },
  });
};
